import React, {useEffect, useState, useTransition} from 'react';
import {useSelector} from "react-redux";

import {useAppKitProvider} from "@reown/appkit/react";
import {useTranslation} from "react-i18next";
import {format} from "date-fns";
import {ethers} from "ethers";

import {
    INFO,
    SIZE,
    ERROR,
    SUCCESS,
    INTERACTION,
    INFORMATION,
    errorHandler,
    STEPS_STATUS,
    BUTTON_VARIANT,
    LISTING_SIGN_TYPE,
    LISTING_DAY_OPTIONS,
    walletAndTransactionErrorHandle
} from "utils";
import {ERC20, KOMPETEMarketplaceV2ABI, KOMPETEGameAssetABI} from "web3/ABI";
import {useUpdateListedOrderMutation} from "store/Bazaar/bazaar.api";
import {useActionContext} from "../useActionContext";
import {CHAIN_TO_PARAMS_MAP} from "web3/chains";
import {useModalsContext} from "layouts";
import {useConnectedWallet} from "hooks";

import {ButtonV2, Information, InputV2, Modal, SelectDropDownDisplay} from "components/atoms";
import {InfoItem, Process} from "./components"

import "../../style.scss"

const BazaarCurrentItemEditListingModal = () => {
    const {isConnectedAddressCorrect} = useConnectedWallet()
    const [isPending, startTransition] = useTransition()
    const {walletProvider} = useAppKitProvider("eip155");
    const {setCurrentModal, currentModal} = useModalsContext()
    const {setActionDetails} = useActionContext()
    const {t} = useTranslation()

    const {userData} = useSelector(state => state.authV2.signIn)
    const {chain, price, inventory, expiration_date, id} = currentModal.data

    const [informationStatus, setInformationStatus] = useState({
        type: INFORMATION.INFO,
        text: INFO.LISTING_EDIT
    })

    const [listingPrice, setListingPrice] = useState("")
    const [expiration, setExpiration] = useState(null)
    const [fee, setFee] = useState(0)

    const [editListing] = useUpdateListedOrderMutation()

    useEffect(() => {
        getFeeDetails().then(res => setFee(res))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    const handleEditForSale = () => {
        startTransition(async () => {
            await startListing()
        })
    }

    const isMatchableWallet = (connectedWallet) => {
        const registeredWallet = userData.wallet.toLowerCase()

        return connectedWallet.toLowerCase() === registeredWallet;
    }

    const startListing = async () => {
        try {
            const chainParamsMap = CHAIN_TO_PARAMS_MAP[chain]
            const {rpcURL, addresses} = chainParamsMap
            const {MARKETPLACE_ADDRESS_V2, COLLECTION_ADDRESS, PAYMENT_ADDRESS} = addresses
            const provider = new ethers.providers.JsonRpcProvider(rpcURL)
            const marketplaceContract = new ethers.Contract(MARKETPLACE_ADDRESS_V2, KOMPETEMarketplaceV2ABI, provider)
            const collectionContract = new ethers.Contract(COLLECTION_ADDRESS, KOMPETEGameAssetABI, provider)
            const paymentContract = new ethers.Contract(PAYMENT_ADDRESS, ERC20, provider)

            const userProvider = new ethers.providers.Web3Provider(walletProvider)
            const signer = userProvider.getSigner()
            const address = await signer.getAddress()

            if (!isMatchableWallet(address)) {
                errorHandler(ERROR.WALLET_CONNECTED_MATCH_INVALID)
            }

            const isApprovedForAll = await collectionContract.isApprovedForAll(address, MARKETPLACE_ADDRESS_V2)

            if (!isApprovedForAll) {
                await collectionContract.connect(signer).setApprovalForAll(MARKETPLACE_ADDRESS_V2, true)
            }

            const [decimals, nonce, marketplaceInfo, lastBlock] = await Promise.all([
                paymentContract.decimals(),
                marketplaceContract.nonces(address),
                marketplaceContract.getMarketplaceInfo(),
                provider.getBlock("latest"),
            ])

            const referrer = ethers.constants.AddressZero
            const price = ethers.utils.parseUnits(listingPrice, decimals)
            const seconds = expiration.value * 24 * 60 * 60
            const deadline = ethers.BigNumber.from(lastBlock.timestamp).add(seconds)

            const lazyData = {
                seller: address,
                tokenContract: COLLECTION_ADDRESS,
                tokenIds: [inventory.token_id],
                amounts: [1],
                price,
                paymentToken: PAYMENT_ADDRESS,
                listingType: 0,
                desiredTokens: [],
                desiredTokenIds: [],
                desiredAmounts: [],
                deadline,
                referrer,
                nonce,
            }

            const domain = {
                name: marketplaceInfo.name,
                version: marketplaceInfo.version,
                chainId: Number(chain),
                verifyingContract: MARKETPLACE_ADDRESS_V2,
            };

            const message = {
                ...lazyData,
                chainId: chain,
            }

            const signature = await signer._signTypedData(domain, LISTING_SIGN_TYPE, message);
            const hash = ethers.utils._TypedDataEncoder.hash(domain, LISTING_SIGN_TYPE, message);
            const recoveredAddress = ethers.utils.recoverAddress(hash, signature);

            if (!isMatchableWallet(recoveredAddress)) {
                errorHandler(ERROR.WALLET_SIGN_FAILED)
            }

            const response = await editListing({
                order_id: id,
                order: lazyData,
                signature,
                deadline: deadline.toNumber(),
            }).unwrap()

            if (response.success) {
                setInformationStatus({type: INFORMATION.SUCCESS, text: SUCCESS.LISTING_EDIT})

                setActionDetails({
                    type: INTERACTION.EDIT,
                    isSuccess: true,
                    data: {
                        id,
                        signature,
                        order: lazyData,
                        price: listingPrice,
                        deadline: deadline.toNumber() * 1000,
                    }
                })

                setTimeout(() => {
                    setCurrentModal({status: STEPS_STATUS.CLOSE})
                }, 2000)
            } else {
                errorHandler(ERROR.WRONG)
            }
        } catch (error) {
            const text = walletAndTransactionErrorHandle(error, false)
            setInformationStatus({type: INFORMATION.ERROR, text})
        }
    }

    const getFeeDetails = async () => {
        try {
            const chainParamsMap = CHAIN_TO_PARAMS_MAP[chain]
            const {rpcURL, addresses} = chainParamsMap
            const {MARKETPLACE_ADDRESS_V2, COLLECTION_ADDRESS} = addresses
            const provider = new ethers.providers.JsonRpcProvider(rpcURL)
            const marketplaceContract = new ethers.Contract(MARKETPLACE_ADDRESS_V2, KOMPETEMarketplaceV2ABI, provider)

            const fee = await marketplaceContract.getFeeConfig(COLLECTION_ADDRESS)

            return (fee.collectionFee.toNumber() + fee.protocolFee.toNumber()) / 100
        } catch {
            return 7.5
        }
    }

    const profitCalculation = () => {
        return listingPrice - (listingPrice * fee) / 100
    }

    const isConfirmButtonDisabled = isPending || informationStatus.type === INFORMATION.SUCCESS || !listingPrice || !expiration || !isConnectedAddressCorrect
    const isDisabled = isPending || informationStatus.type === INFORMATION.SUCCESS

    return (
        <Modal
            isOpen={true}
            size={SIZE.SMALL}
            title={t("editListingModalTitle")}
            onClose={() => isDisabled ? null : setCurrentModal({status: STEPS_STATUS.CLOSE})}
        >
            <div className="bazaar-current-item_make-listing-modal">
                <span className="bazaar_current-item_make-listing-modal_under-title">{t("editListingModalSubTitle")}</span>
                <div className="bazaar_current-item_make-listing-modal_item-info">
                    <InfoItem name="item" value={inventory.name}/>
                    <InfoItem name="tokenId" value={inventory.token_id}/>
                    <InfoItem name="price" value={`${price} KOMPETE`}/>
                    <InfoItem name="expiration" value={format(new Date(expiration_date), 'yyyy-MM-dd')}/>
                </div>
                <div className="bazaar_current-item_make-listing-modal_inps">
                    <InputV2
                        name="amount"
                        type="number"
                        size={SIZE.MEDIUM}
                        value={listingPrice}
                        label={t("listingPrice")}
                        disabled={!fee || isDisabled}
                        onChange={(e) => setListingPrice(e.target.value)}
                    />
                    <span>{t("youWillReceive")}: ~{profitCalculation()} KOMPETE</span>
                    <SelectDropDownDisplay
                        size="md"
                        value={expiration}
                        disabled={isDisabled}
                        isPrepareOptionsForLang={true}
                        optionsList={LISTING_DAY_OPTIONS}
                        label={t("listingExpiration")}
                        onChange={(e) => setExpiration(e.target.value.value)}
                    />
                </div>
                {isPending ? <Process/> : <Information
                    type={informationStatus.type}
                    text={informationStatus.text}
                />}
                <div className="bazaar_current-item_make-listing-modal_btns">
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        onClick={() => setCurrentModal({status: STEPS_STATUS.CLOSE})}
                        disabled={isPending || informationStatus.type === INFORMATION.SUCCESS}
                    >
                        cancel
                    </ButtonV2>
                    <ButtonV2
                        size={SIZE.MEDIUM}
                        onClick={handleEditForSale}
                        variant={BUTTON_VARIANT.RED}
                        disabled={isConfirmButtonDisabled}
                    >
                        save
                    </ButtonV2>
                </div>
            </div>
        </Modal>
    )
}

export default BazaarCurrentItemEditListingModal